import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { gsap } from "gsap";
import AbtBgImg from "../../assets/img/website-images/few-words-about-akash-pharmacy-home-page.png";

/* ─── Shared hook: triggers when 20% of element is visible ─────── */
function useScrollReveal() {
  return useInView({ threshold: 0.2, triggerOnce: true });
}

/* ─── Framer Motion Variants ───────────────────────────────────── */
const fadeInLeft = {
  hidden: { opacity: 0, x: -70 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
  },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 70 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.85, ease: [0.22, 1, 0.36, 1] },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: "easeOut", delay },
  }),
};

const listContainerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.22, delayChildren: 0.3 },
  },
};

const listItemVariants = {
  hidden: { opacity: 0, x: 40 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  },
};

/* ─── Badge Counter (GSAP number count-up) ─────────────────────── */
function YearsBadge() {
  const numRef = useRef(null);
  const { ref: inViewRef, inView } = useScrollReveal();

  const setRefs = (el) => {
    numRef.current = el;
    inViewRef(el);
  };

  useEffect(() => {
    if (inView && numRef.current) {
      gsap.fromTo(
        numRef.current,
        { innerText: 0 },
        {
          innerText: 2,
          duration: 1.2,
          ease: "power2.out",
          snap: { innerText: 1 },
          onUpdate() {
            if (numRef.current) {
              numRef.current.innerText = Math.round(
                this.targets()[0].innerText,
              );
            }
          },
        },
      );
    }
  }, [inView]);

  return (
    <>
    <div className="about-text second-about">
      <span>
        {/* <span ref={setRefs}>7</span>  */}
        <span>7 </span>
        <sub> +</sub>
      </span>
      <p>Years of Excellence</p>
    </div>
    </>
  );
}

/* ─── Main Component ───────────────────────────────────────────── */
function About() {
  /* Left image column */
  const { ref: leftRef, inView: leftInView } = useScrollReveal();

  /* Right content column */
  const { ref: rightRef, inView: rightInView } = useScrollReveal();

  /* Decorative background animation (GSAP floating) */
  const bgRef = useRef(null);
  useEffect(() => {
    if (bgRef.current) {
      gsap.to(bgRef.current, {
        y: -18,
        duration: 3.5,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });
    }
  }, []);

  return (
    <section
      className="about-area about-p pt-120 pb-20 p-relative fix"
      style={{ background: "#eff7ff" }}
    >
      {/* Floating decorative image */}
      <div className="animations-02" ref={bgRef}>
        <img src="/assets/img/bg/an-img-02.png" alt="contact-bg-an-01" />
      </div>

      <div className="container">
        <div className="row justify-content-center align-items-center">
          {/* ── Left: Image ── */}
          <div className="col-lg-6 col-md-12 col-sm-12">
            <motion.div
              ref={leftRef}
              className="s-about-img p-relative"
              variants={fadeInLeft}
              initial="hidden"
              animate={leftInView ? "visible" : "hidden"}
            >
              <motion.img
                src={AbtBgImg}
                alt="img"
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.4 }}
              />
              {/* Badge pops in with spring */}
              <motion.div
                initial={{ opacity: 0, scale: 0.5 }}
                animate={
                  leftInView
                    ? { opacity: 1, scale: 1 }
                    : { opacity: 0, scale: 0.5 }
                }
                transition={{
                  duration: 0.6,
                  ease: [0.34, 1.56, 0.64, 1],
                  delay: 0.5,
                }}
              >
                <YearsBadge />
              </motion.div>
            </motion.div>
          </div>

          {/* ── Right: Content ── */}
          <div className="col-lg-6 col-md-12 col-sm-12">
            <motion.div
              ref={rightRef}
              className="about-content s-about-content pl-15"
              variants={fadeInRight}
              initial="hidden"
              animate={rightInView ? "visible" : "hidden"}
            >
              {/* Title block */}
              <div className="about-title second-title pb-25">
                <motion.h5
                  variants={fadeUp}
                  custom={0.1}
                  initial="hidden"
                  animate={rightInView ? "visible" : "hidden"}
                >
                  <i className="fal fa-graduation-cap" /> About Our Institute
                </motion.h5>

                <motion.h2
                  variants={fadeUp}
                  custom={0.2}
                  initial="hidden"
                  animate={rightInView ? "visible" : "hidden"}
                >
                  A Few Words About Akash Institute of Pharmacy
                </motion.h2>
              </div>

              {/* Paragraphs */}
              <motion.p
                className="txt-clr"
                variants={fadeUp}
                custom={0.3}
                initial="hidden"
                animate={rightInView ? "visible" : "hidden"}
              >
                Established in 2019 under the Akash Educational & Development Trust, Akash Institute of Pharmacy (AIP) is a premier centre for pharmaceutical education functioning under the Akash Group of Institutions. The institute is committed to delivering quality education with a strong focus on academic excellence, practical training, and professional development.
              </motion.p>

              <motion.p
                variants={fadeUp}
                custom={0.4}
                initial="hidden"
                animate={rightInView ? "visible" : "hidden"}
              >
                Affiliated to Rajiv Gandhi University of Health Sciences (RGUHS) and recognized by the Pharmacy Council of India (PCI), the institute strives to prepare students for successful careers in healthcare and pharmaceutical sciences through modern teaching methodologies, well-equipped laboratories, and industry-oriented learning.
              </motion.p>

              {/* List items — staggered */}
              <div className="about-content2">
                <div className="row">
                  <div className="col-md-12">
                    <motion.ul
                      className="green2"
                      variants={listContainerVariants}
                      initial="hidden"
                      animate={rightInView ? "visible" : "hidden"}
                    >
                      <motion.li variants={listItemVariants}>
                        <div className="abcontent">
                          <div className="ano">
                            <span>01</span>
                          </div>
                          <div className="text">
                            <h3>PCI Recognized Program</h3>
                            <p>
                              A fully accredited B.Pharm program recognized by
                              the Pharmacy Council of India, ensuring nationally
                              valued qualifications.
                            </p>
                          </div>
                        </div>
                      </motion.li>

                      <motion.li variants={listItemVariants}>
                        <div className="abcontent">
                          <div className="ano">
                            <span>02</span>
                          </div>
                          <div className="text">
                            <h3>Industry-Driven Learning</h3>
                            <p>
                              Hands-on laboratory training, research exposure,
                              and strong industry partnerships that prepare
                              graduates for diverse careers in healthcare and
                              pharma.
                            </p>
                          </div>
                        </div>
                      </motion.li>
                    </motion.ul>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
              <motion.div
                className="slider-btn mt-20"
                variants={fadeUp}
                custom={0.65}
                initial="hidden"
                animate={rightInView ? "visible" : "hidden"}
              >
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  style={{ display: "inline-block" }}
                >
                  <Link to="/about-us" className="btn ss-btn smoth-scroll">
                    Read More <i className="fal fa-long-arrow-right" />
                  </Link>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default About;
